import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@design-system/Dialog';

import {
  type EducationalContent,
  EDUCATIONAL_CONTENT_TYPE,
} from '@viome/viome-base/src/usecase/Education/EducationModel';

import { EventTracker } from '@/utils';
import { MediaCard } from '@design-system/Cards/MediaCard';
import { Link } from '@design-system/Link';
import { Skeleton } from '@design-system/Skeleton';
import VideoPlayer from '@design-system/VideoPlayer';
import { useGetJourneyEducationalContent } from '@viome/viome-base/src/usecase/Education/EducationQuery';
import { useState } from 'react';

interface NutritionJourneyDeeperSectionProp {
  sectionInfo: string;
  screen: string;
  className?: string;
}

const setGAEvent = (screen: string, title: string) => {
  return EventTracker.trackEvent(
    screen,
    `${screen}_jny_deeper_article_${title.slice(0, 3)}`,
  );
};

const handleMediaRedirection = (
  title: string,
  link: string,
  srceen: string,
) => {
  setGAEvent(srceen, title);
  return link;
};

const renderMedias = (
  medias?: EducationalContent[],
  isLoading?: boolean,
  onClick: (media: EducationalContent) => void,
  screen: string,
  className?: string,
) => {
  if (isLoading) {
    return Array.from({ length: 4 }).map((_, index) => (
      <Skeleton key={index} className="w-full h-32" />
    ));
  }

  if (!medias?.length) return null;

  const renderedMedias = medias.map((media, index) => {
    if (media.footer_type === EDUCATIONAL_CONTENT_TYPE.VIDEO) {
      return (
        <DialogTrigger
          key={`${media.title}-${index}`}
          onClick={() => {
            setGAEvent(screen, media.title);
            onClick(media);
          }}
        >
          <MediaCard
            title={media.title}
            description={media.subtitle}
            thumbnailImage={media.image_url}
            className={className}
          />
        </DialogTrigger>
      );
    }

    return (
      <Link
        variant="flat"
        to={handleMediaRedirection(media.title, media.link, screen)}
        target="_blank"
        key={`${media.title}-${index}`}
      >
        <MediaCard
          title={media.title}
          description={media.subtitle}
          thumbnailImage={media.image_url}
          className={className}
        />
      </Link>
    );
  });

  return renderedMedias;
};

export const NutritionJourneyDeeperSection = ({
  sectionInfo,
  screen,
  className = '',
}: NutritionJourneyDeeperSectionProp) => {
  const {
    data: journeyData,
    isLoading,
    isError: isJourneyDataError,
  } = useGetJourneyEducationalContent(sectionInfo);
  const [selectedMedia, setSelectedMedia] = useState<EducationalContent>();

  if (isJourneyDataError) {
    return;
  }

  if (!isLoading && !journeyData?.length) return;

  return (
    <Dialog>
      {isLoading ? (
        <Skeleton className="h-12 w-1/4 mb-6 mt-12" />
      ) : (
        <p className="mt-12 mb-6 typography-h2 text-primary">Journey Deeper</p>
      )}

      <div className="gap-2 grid grid-cols-1 md:grid-cols-2">
        {renderMedias(
          journeyData,
          isLoading,
          setSelectedMedia,
          screen,
          className,
        )}
      </div>

      {selectedMedia && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedMedia.title}</DialogTitle>
          </DialogHeader>

          <VideoPlayer
            controls
            className="aspect-video"
            videoSrc={selectedMedia.link}
            poster={selectedMedia.image_url}
          />

          {selectedMedia.description && (
            <DialogDescription>{selectedMedia.description}</DialogDescription>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
};
